import { MDBIcon, MDBInputGroup } from "mdb-react-ui-kit";
import { useContext, useState } from "react";
import { toast } from "react-toastify";
import useResumeBusinessSkillsHandlers from "../../../../context/resume/hooks/resume-business-skills-handlers";
import { ResumeContext } from "../../../../context/resume/resume-context";
import { TranslationContext } from "../../../../context/translations/translations-context";
import useResumeDocumentSection from "../../../../hooks/useResumeDocumentSection";
import ReferentialSelect from "../../../assets/ReferentialSelect";

export default function CategoryPicker() {
    const { translations, displayLanguage } = useContext( TranslationContext );
    const {
        refCompetences,
        refCompetencesCategories,
    } = useContext( ResumeContext );
    const { handleAddCategory, handleAddCompetence, } = useResumeBusinessSkillsHandlers();

    const { businessSkills } = useResumeDocumentSection();

    const [ selectedCategory, setSelectedCategory ] = useState( 0 );
    const [ isSearching, setIsSearching ] = useState( false );
    const [ searchInput, setSearchInput ] = useState( "" );
    const [ searchResults, setSearchResults ] = useState( [] );
    const [ activeSuggestionIndex, setActiveSuggestionIndex ] = useState( -1 );
    const getCategoryLabel = ( category ) => (
        category?.translations?.[ displayLanguage ]
        || category?.translations?.fr
        || category?.translations?.en
        || ""
    );
    const getCompetenceLabel = ( competence ) => (
        competence?.competenceTranslations?.[ displayLanguage ]
        || competence?.competenceTranslations?.fr
        || competence?.competenceTranslations?.en
        || ""
    );

    const handleKeyDown = ( e ) => {
        if ( e.key === "ArrowDown" && activeSuggestionIndex < searchResults.length - 1 ) {
            setActiveSuggestionIndex( activeSuggestionIndex + 1 );
        } else if ( e.key === "ArrowUp" && activeSuggestionIndex > 0 ) {
            setActiveSuggestionIndex( activeSuggestionIndex - 1 );
        } else if ( e.key === "Enter" && activeSuggestionIndex !== -1 ) {
            e.preventDefault();
            addSelectedCompetence( searchResults[ activeSuggestionIndex ] );
        }
    };

    const handleMouseEnter = ( index ) => {
        setActiveSuggestionIndex( index );
    };

    const handleClick = ( index ) => {
        addSelectedCompetence( searchResults[ index ] );
    }

    const addSelectedCompetence = ( value ) => {
        const foundCategory = refCompetencesCategories.find( cat => cat.id === value.categoryId );
        const foundCompetence = refCompetences.find( com => com.id === value.id );
        handleAddCategoryAndCompetence(
            foundCategory,
            {
                id: foundCompetence.id,
                translations: foundCompetence.competenceTranslations,
                level: 0
            }
        )
        setSearchInput( '' );
        setSearchResults( [] );
    }

    const handleSearchTermChange = ( searchTerm ) => {
        setSearchInput( searchTerm )

        // only search if the input is at least 3 characters long
        if ( searchTerm.length > 2 ) {
            setIsSearching( true );
            setSearchResults(
                refCompetences
                    .filter( c => getCompetenceLabel( c ).toLowerCase().indexOf( searchTerm.toLowerCase() ) > -1 )
            );
            setIsSearching( false );
        } else {
            setSearchResults( [] );
        }
    };

    function handleAddCategoryAndCompetence( category, competence ) {
        if ( !category.id ) {
            toast.warning( translations.businessSkillsSection.selectCategoryLabel );
            return;
        }

        if ( !competence.id ) {
            toast.warning( translations.businessSkillsSection.selectCompetenceLabel );
            return;
        }

        const alreadySelected = businessSkills
            .some( c => c.category.id === Number.parseInt( category.id ) );

        if ( !alreadySelected ) {
            handleAddCategory( category );
        }

        const alreadySelectedCompetence = businessSkills
            .find( c => c.category.id === Number.parseInt( category.id ) )
            .competences.some( c => c.id === Number.parseInt( competence.id ) );

        if ( alreadySelectedCompetence ) {
            toast.warning( translations.businessSkillsSection.competenceAlreadySelectedLabel );
            return;
        }

        const parentOrder = businessSkills
            .find( c => c.category.id === Number.parseInt( category.id ) )
            .order;

        handleAddCompetence( competence, parentOrder );
    }

    function handleAddSelectedCategory( category ) {
        if ( !category.id ) {
            toast.warning( translations.businessSkillsSection.selectCategoryLabel );
            return;
        }

        const alreadySelected = businessSkills
            .some( c => c.category.id === Number.parseInt( category.id ) );

        if ( alreadySelected ) {
            toast.warning( translations.businessSkillsSection.categoryAlreadySelected );
            return;
        }

        handleAddCategory( category );
    }

    return (
        <div>
            <label>{ translations.businessSkillsSection.title }</label>
            <div className="d-flex align-items-center gap-1">
                <ReferentialSelect
                    name="competencesCategories"
                    elementId="competencesCategories"
                    firstOption={ translations.businessSkillsSection.selectCategoryLabel }
                    selectedItem={ selectedCategory }
                    referential={ refCompetencesCategories
                        .sort( ( a, b ) =>
                            getCategoryLabel( a ).localeCompare( getCategoryLabel( b ), displayLanguage )
                        ) }
                    displayLanguage={ displayLanguage }
                    handleChange={ ( e ) => setSelectedCategory( Number.parseInt( e.target.value ) ) }
                    className={ `form-select select-input w-50 ${ !selectedCategory ? 'border-danger' : '' }` }
                />
                <button type="submit" className="btn_primary" onClick={ () => {
                    if ( selectedCategory ) {
                        handleAddSelectedCategory( refCompetencesCategories.find( category => category.id === selectedCategory ) );
                    } else {
                        toast.warning( translations.businessSkillsSection.selectCategoryLabel );
                    }
                } }>
                    <MDBIcon fas icon="plus" title={ translations.businessSkillsSection.addCategoryLabel }
                             color='light'/>
                </button>
                <p className="ms-3 me-1 pt-3">ou</p>
                <div style={ { position: "relative" } }>
                    <div className="pt-3">
                        <MDBInputGroup
                            className='mb-3' noBorder
                            textBefore={ <MDBIcon fas icon='search' className='text-white'/> }>
                            <input
                                className='form-control text-white'
                                style={ { backgroundColor: 'transparent' } }
                                type='text'
                                placeholder={ translations.businessSkillsSection.searchCompetenceLabel }
                                value={ searchInput }
                                onChange={ ( e ) => handleSearchTermChange( e.target.value ) }
                                onKeyDown={ handleKeyDown }
                            />
                        </MDBInputGroup>
                    </div>
                    <div className="suggestions-box-edition" style={ { maxHeight: '300px', overflowY: 'auto' } }>
                        { searchResults && searchResults.map( ( result, index ) => (
                            <div
                                style={ { maxHeight: '300px', overflowY: 'auto' } }
                                onClick={ () => handleClick( index ) }
                                onMouseEnter={ () => handleMouseEnter( index ) }
                            >
                                <p
                                    className="px-3"
                                    style={ {
                                        maxHeight: '300px',
                                        overflowY: 'auto',
                                        backgroundColor: index === activeSuggestionIndex ? '#09B4BF' : 'transparent'
                                    } }
                                >{ getCompetenceLabel( result ) }</p>
                            </div>
                        ) ) }
                    </div>
                </div>
            </div>
        </div>
    );
}
